/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Cookies from "js-cookie";
import {
  Buildings, Receipt, CreditCard, Sparkle, ArrowRight, SignOut, EnvelopeSimple,
  UploadSimple, Trash, Bell, BellRinging, BellSlash, House, Camera, HardDrives,
  CheckCircle, Circle, X, Globe, Copy, QrCode, Question,
  ShieldCheck as ShieldCheckTab, DeviceMobile, Browser,
} from "@phosphor-icons/react";
import { resetAllDismissedIntros } from "@/components/DismissibleIntro";
import { resetAllHints } from "@/components/DismissibleHint";
import usePushNotifications from "@/hooks/usePushNotifications";
import { isNative } from "@/lib/native";
import BrandColorPicker from "@/components/BrandColorPicker";
import LandingPersonaleTab from "@/components/LandingPersonaleTab";
import PortabilityTab from "@/components/PortabilityTab";
import { isReaderMode, READER_MODE_NOTICE } from "@/lib/iosReader";

const TABS = [
  { id: "agency", label: "Profilo & agenzia", icon: Buildings },
  { id: "landing", label: "La tua pagina personale", icon: Globe },
  { id: "portability", label: "I miei dati · sempre tuoi", icon: ShieldCheckTab },
  { id: "billing", label: "Dati fatturazione", icon: Receipt },
  { id: "account", label: "Account & Abbonamento", icon: CreditCard },
];

const PLAN_LABEL = {
  free: { name: "Free", desc: "10 crediti / mese · 1 immobile", credits: 10 },
  // iter v33 r106 · Piano-ombra promo (entitlement 60 crediti lancio):
  // in UI mostrato come "Free" — l'utente NON è mai stato Pro/trial.
  promo: { name: "Free", desc: "10 crediti / mese · 1 immobile", credits: 10 },
  base: { name: "Start", desc: "150 crediti / mese · fino a 5 immobili", credits: 150 },
  pro:  { name: "Pro",  desc: "320 crediti / mese · Assistente illimitato · immobili senza limite", credits: 320 },
};

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("agency");
  const tabContentRef = useRef(null);
  const [form, setForm] = useState({
    agency_name: "", agent_display_name: "", agency_website: "",
    agency_phone: "", agency_email: "", agent_mobile: "", agency_logo_url: "", agent_photo_url: "", agency_address: "",
    brand_color: "",
    page_bg: "auto",
    default_listing_style: "",
    billing_legal_name: "", billing_vat: "", billing_tax_code: "", billing_address: "",
    billing_sdi: "", billing_pec: "", billing_country: "Italia",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        agency_name: user.agency_name || "",
        agent_display_name: user.agent_display_name || "",
        agency_website: user.agency_website || "",
        agency_phone: user.agency_phone || "",
        agency_email: user.agency_email || "",
        agent_mobile: user.agent_mobile || "",
        agency_logo_url: user.agency_logo_url || "",
        agent_photo_url: user.agent_photo_url || "",
        agency_address: user.agency_address || "",
        brand_color: user.brand_color || "",
        page_bg: user.page_bg || "auto",
        default_listing_style: user.default_listing_style || "moderno",
        billing_legal_name: user.billing_legal_name || "",
        billing_vat: user.billing_vat || "",
        billing_tax_code: user.billing_tax_code || "",
        billing_address: user.billing_address || "",
        billing_sdi: user.billing_sdi || "",
        billing_pec: user.billing_pec || "",
        billing_country: user.billing_country || "Italia",
      }));
    }
  }, [user]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // iter v27 r9 — Fix bug "nome agenzia che ricompare dopo save".
      // Il backend interpreta "" come "ignora questo campo" (per evitare che
      // un form ricaricato vuoti i dati prima dell'idratazione). Quando però
      // l'utente svuota DELIBERATAMENTE un campo, dobbiamo mandare `null` per
      // dire "cancellalo davvero". I campi `email` e `country` non sono
      // null-abili (default + required).
      const NULLABLE = new Set([
        "agency_name", "agent_display_name", "agency_website",
        "agency_phone", "agency_email", "agent_mobile",
        "agency_address",
        "billing_legal_name", "billing_vat", "billing_tax_code",
        "billing_address", "billing_sdi", "billing_pec",
      ]);
      const payload = { ...form };
      for (const k of Object.keys(payload)) {
        if (NULLABLE.has(k) && typeof payload[k] === "string" && payload[k].trim() === "") {
          payload[k] = null;
        }
      }
      await api.put("/profile/agency", payload);
      await refresh();
      toast.success(tab === "agency" ? "Profilo aggiornato" : "Dati fatturazione aggiornati");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const [profileBannerDismissed, setProfileBannerDismissed] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("pronto_profile_banner_dismissed") === "1"
  );

  const planInfo = PLAN_LABEL[user?.plan] || PLAN_LABEL.free;
  const creditsUsed = user?.ai_photos_used_this_month || 0;
  const creditsTotal = planInfo.credits;
  const creditsLeft = Math.max(0, creditsTotal - creditsUsed);

  // Profile completion checklist (banner)
  const profileSteps = [
    { id: "name", done: !!(form.agent_display_name?.trim() || form.agency_name?.trim()), label: "Il tuo nome (e/o agenzia)" },
    { id: "contact", done: !!form.agency_phone?.trim() && !!form.agency_email?.trim(), label: "Telefono ed email" },
    { id: "logo", done: !!form.agency_logo_url?.trim(), label: "Logo agenzia" },
    { id: "address", done: !!form.agency_address?.trim(), label: "Indirizzo agenzia" },
  ];
  const profileDone = profileSteps.filter((s) => s.done).length;
  const profilePct = Math.round((profileDone / profileSteps.length) * 100);
  const showProfileBanner = tab === "agency" && profileDone < profileSteps.length && !profileBannerDismissed;
  const dismissProfileBanner = () => {
    localStorage.setItem("pronto_profile_banner_dismissed", "1");
    setProfileBannerDismissed(true);
  };

  return (
    <AppShell title="Profilo" breadcrumb="Profilo">
        <span className="overline">Profilo</span>
        <h1 className="font-editorial text-4xl lg:text-5xl tracking-tight mt-2" data-testid="profile-title">
          Il tuo <span className="italic-accent text-[#7C8B6E]">spazio</span>.
        </h1>
        <p className="mt-3 text-[#6B6A65]">
          Gestisci agenzia, dati fatturazione e abbonamento da qui.
          <span className="block mt-2 text-[13px] text-[#3F3D38] bg-[#F1F4ED] border-l-2 border-[#7C8B6E] pl-3 py-2 rounded-r">
            <strong>Perché completare il profilo?</strong> Logo, nome e contatti vengono usati automaticamente sulle <strong>pagine annuncio pubbliche</strong>, nelle <strong>PDF stampabili</strong> e nelle email che mandi ai clienti. Più è completo, più sembri professionale.
          </span>
        </p>

        {/* Profile completion banner — auto-hides at 100% or on dismiss */}
        {showProfileBanner && (
          <div
            className="mt-6 bg-white border border-[#7C8B6E]/30 rounded-lg p-4 lg:p-5 relative shadow-sm"
            data-testid="profile-completion-banner"
            data-completion-pct={profilePct}
          >
            <button
              onClick={dismissProfileBanner}
              className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center text-[#9A968B] hover:text-[#1C1C1A] hover:bg-[#F1F4ED] rounded-full transition-colors"
              data-testid="profile-banner-dismiss"
              aria-label="Chiudi e non mostrare più"
              title="Non mostrare più"
            >
              <X size={13} weight="bold" />
            </button>

            <div className="flex items-center gap-2.5 mb-3 pr-7">
              <div className="w-8 h-8 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-full flex items-center justify-center shrink-0">
                <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold">
                  Profilo · {profileDone}/{profileSteps.length}
                </div>
                <div className="text-[14px] text-[#1C1C1A] font-semibold leading-tight">
                  Più completo è, più professionale appari ai clienti
                </div>
              </div>
            </div>

            <div className="h-1 bg-[#F1F4ED] rounded-full overflow-hidden mb-3.5" aria-hidden="true">
              <div
                className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                style={{ width: `${profilePct}%` }}
                data-testid="profile-progress-bar"
              />
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="profile-steps">
              {profileSteps.map((s) => (
                <li
                  key={s.id}
                  className={`flex items-center gap-2 text-[12.5px] ${s.done ? "opacity-55" : "text-[#1C1C1A]"}`}
                  data-testid={`profile-step-${s.id}`}
                  data-step-done={s.done ? "true" : "false"}
                >
                  {s.done ? (
                    <CheckCircle size={15} weight="fill" className="text-[#7C8B6E] shrink-0" />
                  ) : (
                    <Circle size={15} weight="regular" className="text-[#C8C5BD] shrink-0" />
                  )}
                  <span className={s.done ? "line-through text-[#9A968B]" : "font-medium"}>{s.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* iter v29 r221 — Auto-scroll alla form quando l'utente cambia tab
            (su mobile la lista tab sta in alto, la form sotto rimaneva
            invisibile). Ref + scrollIntoView smooth. */}
        <div ref={tabContentRef} />

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-[#E5E7E0] pb-2 overflow-x-auto hide-scroll">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                // Su mobile scrolla la form in vista dopo il render
                setTimeout(() => {
                  tabContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-[15px] font-semibold transition-colors whitespace-nowrap ${
                tab === t.id ? "bg-[#7C8B6E] text-[#FFFFFF]" : "text-[#1C1C1A] hover:bg-[#F1F4ED]"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={17} weight={tab === t.id ? "fill" : "light"} /> {t.label}
            </button>
          ))}
        </div>

        {/* AGENCY TAB */}
        {tab === "agency" && (
          <form onSubmit={save} className="mt-8 card-flat p-8 space-y-5" data-testid="agency-form">
            <div>
              <h2 className="font-editorial text-2xl">Come ti mostri ai clienti</h2>
              <p className="text-sm text-[#6B6A65] mt-1">
                I tuoi dati pubblici. Appaiono sulle pagine annuncio, nelle PDF stampabili e nelle email di sopralluogo.
                <strong className="block mt-1 text-[#1C1C1A]">Lavori da solo?</strong>
                Va bene lo stesso — compila solo "Il tuo nome", lascia "Nome agenzia" vuoto.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Il tuo nome <span className="font-normal lowercase text-[#9A968B]">· obbligatorio</span></label>
                <input data-vista value={form.agent_display_name} onChange={onChange("agent_display_name")} placeholder="Es. Marco Ferrari" data-testid="agent-display-name-input" />
                <p className="mt-1.5 text-[11px] text-[#9A968B] leading-snug">Il nome con cui ti firmi sui contenuti.</p>
              </div>
              <div>
                <label className="overline block mb-2">Nome agenzia <span className="font-normal lowercase text-[#9A968B]">· opzionale</span></label>
                <input data-vista value={form.agency_name} onChange={onChange("agency_name")} placeholder="Es. Studio Ferrari" data-testid="agency-name-input" />
                <p className="mt-1.5 text-[11px] text-[#9A968B] leading-snug">Lascia vuoto se lavori da solo.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Telefono pubblico</label>
                <input data-vista type="tel" value={form.agency_phone} onChange={onChange("agency_phone")} placeholder="+39 02 1234 5678" data-testid="agency-phone-input" />
                <p className="mt-1.5 text-[11px] text-[#9A968B] leading-snug">Centralino o numero fisso dell'agenzia.</p>
              </div>
              <div>
                <label className="overline block mb-2">Email pubblica</label>
                <input data-vista type="email" value={form.agency_email} onChange={onChange("agency_email")} placeholder="info@agenzia.it" data-testid="agency-email-input" />
                <p className="mt-1.5 text-[11px] text-[#9A968B] leading-snug">Dove ti arrivano i lead via email.</p>
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Cellulare diretto <span className="font-normal lowercase text-[#9A968B]">· consigliato</span></label>
              <input data-vista type="tel" value={form.agent_mobile} onChange={onChange("agent_mobile")} placeholder="+39 333 1234567" data-testid="agent-mobile-input" />
              <p className="mt-1.5 text-[11.5px] text-[#6B6A65] leading-snug">
                Il tuo cellulare personale per WhatsApp e chiamate dirette. Appare su Post, Carosello, Story e Brochure.
                Se compilato, ha la <strong>priorità sul centralino</strong> perché i lead da social preferiscono il diretto.
              </p>
            </div>
            <div>
              <label className="overline block mb-2">Sito web dell'agenzia <span className="font-normal lowercase text-[#9A968B]">· opzionale</span></label>
              <input data-vista value={form.agency_website} onChange={onChange("agency_website")} placeholder="miaagenzia.it · o https://miaagenzia.it" data-testid="agency-website-input" />
              <p className="mt-1.5 text-[11.5px] text-[#6B6A65] leading-snug">
                Appare come link cliccabile sulla pagina pubblica dell&apos;annuncio e nelle email che mandi ai clienti. Aggiungi <em>https://</em> oppure no, lo completiamo noi.
              </p>
            </div>
            <div>
              <label className="overline block mb-2">Indirizzo agenzia</label>
              <input data-vista value={form.agency_address} onChange={onChange("agency_address")} placeholder="Via Roma 42, 20100 Milano" data-testid="agency-address-input" />
            </div>
            <div>
              <label className="overline block mb-2">Logo agenzia</label>
              <LogoUploader
                value={form.agency_logo_url}
                onChange={(newUrl) => setForm((f) => ({ ...f, agency_logo_url: newUrl }))}
                onRefresh={refresh}
                userId={user?.user_id}
              />
              <p className="mt-2 text-xs text-[#6B6A65]">
                PNG, JPG, WebP o SVG · max 2MB. Il logo appare sulle pagine pubbliche degli immobili, nelle PDF stampabili e nelle email di sopralluogo.
              </p>
            </div>
            <div>
              <label className="overline block mb-2">La tua foto <span className="font-normal lowercase text-[#9A968B]">· opzionale</span></label>
              <AgentPhotoUploader
                value={form.agent_photo_url}
                onChange={(newUrl) => setForm((f) => ({ ...f, agent_photo_url: newUrl }))}
                onRefresh={refresh}
                userId={user?.user_id}
              />
              <p className="mt-2 text-xs text-[#6B6A65]">
                Un ritratto professionale (JPG, PNG, WebP · max 4MB). Appare al posto della foto Google sulle pagine annuncio e nelle email.
                <strong className="block mt-1 text-[#1C1C1A]">La tua foto Google non viene mai mostrata nei materiali pubblici.</strong>
              </p>
            </div>
            <div>
              <label className="overline block mb-2">Colore brand</label>
              <BrandColorPicker
                variant="circle"
                value={form.brand_color}
                onChange={(color) => setForm((f) => ({ ...f, brand_color: color }))}
              />
              <p className="mt-2 text-xs text-[#6B6A65]">
                Il colore del tuo brand. Viene usato nei contenuti social (Post, Reel, Story) per dare identità alla tua agenzia. Se non lo scegli, usiamo il sage Pronto.
              </p>
            </div>
            {/* iter r96f · User M1514+M1516 — sfondo pagina indipendente dallo skin.
                Alcune combo (Moderno + bg chiaro / Minimal + bg nero) rendono
                i testi hardcoded illeggibili → disabilitiamo con opacity 0.35
                + tooltip esplicativo. */}
            <div>
              <label className="overline block mb-2">Sfondo pagina sito</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { key: "auto",        label: "Auto",      hex: "linear-gradient(135deg,#F5F1EA 0%,#F5F1EA 33%,#FFFFFF 33%,#FFFFFF 66%,#0F0F0F 66%,#0F0F0F 100%)" },
                  { key: "cream",       label: "Cream",     hex: "#F5F1EA" },
                  { key: "white",       label: "Bianco",    hex: "#FFFFFF" },
                  { key: "light-grey",  label: "Grigio",    hex: "#EDEDEA" },
                  { key: "black",       label: "Nero",      hex: "#0F0F0F" },
                ].map(({ key, label, hex }) => {
                  const active = (form.page_bg || "auto") === key;
                  // Compatibility matrix: skin scure con bg chiari (o viceversa)
                  // hanno testi hardcoded che diventano illeggibili.
                  const skin = form.default_listing_style || "moderno";
                  const bgIsDark = key === "black";
                  const bgIsLight = ["cream", "white", "light-grey"].includes(key);
                  const skinIsDark = skin === "moderno";
                  const skinIsLight = ["elegante", "luxury", "minimal"].includes(skin);
                  const incompatible =
                    (skinIsDark && bgIsLight) ||   // Moderno + bg chiaro = testi bianchi invisibili
                    (skin === "minimal" && bgIsDark); // Minimal + nero = testi scuri invisibili
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={incompatible}
                      onClick={() => setForm((f) => ({ ...f, page_bg: key }))}
                      className={`flex flex-col items-center gap-1.5 group ${incompatible ? "opacity-35 cursor-not-allowed" : ""}`}
                      title={incompatible ? `Non compatibile con lo skin "${skin}" — testi diventerebbero illeggibili` : label}
                      data-testid={`page-bg-${key}`}
                    >
                      <span
                        className="w-11 h-11 rounded-full transition-all border"
                        style={{
                          background: hex,
                          boxShadow: active
                            ? "0 0 0 3px #1C1C1A, 0 0 0 5px #FAFAF7 inset"
                            : "0 1px 3px rgba(28,28,26,0.08)",
                          borderColor: hex === "#FFFFFF" ? "rgba(28,28,26,0.14)" : "transparent",
                        }}
                        aria-label={label}
                      />
                      <span className="text-[11px] text-[#6B6A65] leading-none">{label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-[#6B6A65]">
                <strong>Auto</strong> = usa lo sfondo di default dello skin (cream su Elegante/Luxury, nero su Moderno, bianco su Minimal).
                Alcune combinazioni sono disattivate perché renderebbero i testi illeggibili.
              </p>
            </div>

            {/* iter v28 r41 — Stile predefinito. iter v28 r146 — mini bars
                4-col grid. iter v28 r297 — UNIFICATO: un'unica scelta vale
                sia per le pagine online sia per i contenuti social (Post,
                Reel, Carousel). User feedback (audio 24/05): "in realtà è
                uno solo, c'è per tutto". Preview arricchita: mini-cartolina
                con titolo + prezzo nel font caratteristico del layout, così
                la scelta diventa visiva invece che astratta. */}
            <div data-testid="default-listing-style-section">
              <label className="overline block mb-2">Stile predefinito</label>
              <p className="text-xs text-[#6B6A65] -mt-1 mb-2.5">Per le pagine online dei tuoi annunci e per i contenuti del Kit (post, reel, carousel).</p>
              <div className="grid grid-cols-4 gap-2 max-w-[440px]">
                {[
                  { v: "moderno",  l: "Moderno" },
                  { v: "elegante", l: "Elegante" },
                  { v: "minimal",  l: "Minimal" },
                  { v: "lussuoso", l: "Lussuoso" },
                ].map(({ v, l }) => {
                  const active = form.default_listing_style === v;
                  // Preview "mini-cartolina": foto stilizzata + titolo + prezzo
                  // resi con il font caratteristico del layout. L'aspetto è
                  // ridotto ma riconoscibile rispetto al risultato finale.
                  const previewStyle = {
                    moderno: {
                      titleFont: '"Inter", system-ui, sans-serif',
                      titleWeight: 700,
                      titleCase: 'uppercase',
                      titleLetter: '0.04em',
                      priceFont: '"Inter", system-ui, sans-serif',
                      priceWeight: 600,
                      align: 'left',
                      accent: '#1C1C1A',
                    },
                    elegante: {
                      titleFont: '"Cormorant Garamond", "Times New Roman", serif',
                      titleWeight: 600,
                      titleCase: 'none',
                      titleLetter: '0',
                      priceFont: '"Inter", system-ui, sans-serif',
                      priceWeight: 500,
                      align: 'left',
                      accent: '#7C8B6E',
                    },
                    minimal: {
                      titleFont: '"Inter", system-ui, sans-serif',
                      titleWeight: 500,
                      titleCase: 'none',
                      titleLetter: '0.01em',
                      priceFont: '"Inter", system-ui, sans-serif',
                      priceWeight: 400,
                      align: 'left',
                      accent: '#1C1C1A',
                    },
                    lussuoso: {
                      titleFont: '"Cormorant Garamond", "Playfair Display", serif',
                      titleWeight: 600,
                      titleCase: 'uppercase',
                      titleLetter: '0.18em',
                      priceFont: '"Cormorant Garamond", serif',
                      priceWeight: 500,
                      align: 'center',
                      accent: '#A47A2C',  // oro
                    },
                  }[v];
                  return (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setForm((f) => ({ ...f, default_listing_style: v }))}
                      className={`relative p-2 rounded-lg border text-left transition-all ${
                        active
                          ? "border-[#7C8B6E] bg-[#F1F4ED] shadow-sm"
                          : "border-[#EAE7DF] hover:border-[#7C8B6E]/40 hover:bg-[#FAFAF7]"
                      }`}
                      data-testid={`default-listing-style-${v}`}
                      aria-pressed={active}
                    >
                      {/* Mini-cartolina: foto stilizzata + impaginazione testuale */}
                      <div className="w-full aspect-[3/4] rounded bg-white border border-[#EAE7DF] overflow-hidden flex flex-col">
                        {/* faux-foto: degradè warm */}
                        <div
                          className="w-full"
                          style={{
                            height: '46%',
                            background: 'linear-gradient(135deg, #E8DFCE 0%, #C9B89A 60%, #A3886A 100%)',
                          }}
                        />
                        {/* testo con font del layout */}
                        <div
                          className="flex-1 px-1.5 pt-1 pb-1 flex flex-col gap-0.5"
                          style={{ textAlign: previewStyle.align, justifyContent: 'center' }}
                        >
                          <div
                            style={{
                              fontFamily: previewStyle.titleFont,
                              fontWeight: previewStyle.titleWeight,
                              textTransform: previewStyle.titleCase,
                              letterSpacing: previewStyle.titleLetter,
                              fontSize: '8px',
                              lineHeight: 1.1,
                              color: '#1C1C1A',
                            }}
                          >
                            Trilocale
                          </div>
                          <div
                            style={{
                              fontFamily: previewStyle.priceFont,
                              fontWeight: previewStyle.priceWeight,
                              fontSize: '7px',
                              lineHeight: 1,
                              color: previewStyle.accent,
                            }}
                          >
                            280.000 €
                          </div>
                          {/* riga decorativa coerente con il layout */}
                          <div
                            style={{
                              height: 1,
                              width: v === 'lussuoso' ? '40%' : v === 'elegante' ? '30%' : '60%',
                              marginTop: 2,
                              alignSelf: previewStyle.align === 'center' ? 'center' : 'flex-start',
                              background: previewStyle.accent,
                              opacity: v === 'minimal' ? 0.4 : 1,
                            }}
                          />
                        </div>
                      </div>
                      <div className="text-[10px] font-semibold text-[#1C1C1A] leading-tight mt-1 text-center">{l}</div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-[#6B6A65]">
                Stesso layout sulla tua vetrina personale e sui post social. Puoi sempre sovrascriverlo per singolo annuncio dal tab "Anteprima" o dall'Editor social.
              </p>
            </div>

            {/* iter 287 — sezione "Stile predefinito foto" rimossa: confondeva
                gli utenti e il valore di default Pronto è già ottimo. Resta
                modificabile per singolo annuncio nel tab Foto. */}
            <div className="flex items-center justify-between pt-4 border-t border-[#E5E7E0]">
              <div className="flex items-center gap-2 text-sm text-[#6B6A65]">
                <Buildings size={16} /> Visibile nelle pagine pubbliche
              </div>
              <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="save-agency-btn">
                {saving ? "Salvataggio..." : "Salva profilo"}
              </button>
            </div>
          </form>
        )}

        {/* LANDING PERSONALE TAB */}
        {tab === "landing" && <LandingPersonaleTab />}

        {tab === "portability" && <PortabilityTab />}

        {/* BILLING TAB */}
        {tab === "billing" && (
          <form onSubmit={save} className="mt-8 card-flat p-8 space-y-5" data-testid="billing-form">
            <div>
              <h2 className="font-editorial text-2xl">Dati per la fatturazione</h2>
              <p className="text-sm text-[#6B6A65] mt-1">
                Compila questi campi se vuoi ricevere fattura intestata. Sono privati e usati solo per emettere fattura.
              </p>
            </div>
            <div>
              <label className="overline block mb-2">Ragione sociale o intestatario</label>
              <input data-vista value={form.billing_legal_name} onChange={onChange("billing_legal_name")} placeholder="Es. Studio Ferrari S.r.l." data-testid="billing-legal-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Partita IVA</label>
                <input data-vista value={form.billing_vat} onChange={onChange("billing_vat")} placeholder="IT12345678901" data-testid="billing-vat-input" />
                {form.billing_vat && !/^(IT)?\d{11}$/i.test(form.billing_vat.replace(/\s/g, "")) && (
                  <p className="text-[11px] text-[#C45A3E] mt-1">Formato non valido. La P.IVA italiana ha 11 cifre (es. <code>IT12345678901</code>).</p>
                )}
              </div>
              <div>
                <label className="overline block mb-2">Codice fiscale</label>
                <input data-vista value={form.billing_tax_code} onChange={onChange("billing_tax_code")} placeholder="RSSMRA85M01H501Z" data-testid="billing-tax-code-input" />
                {form.billing_tax_code && !/^([A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]|\d{11})$/i.test(form.billing_tax_code.replace(/\s/g, "")) && (
                  <p className="text-[11px] text-[#C45A3E] mt-1">Formato non valido. 16 caratteri (persona fisica) o 11 cifre (azienda).</p>
                )}
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Indirizzo sede legale</label>
              <input data-vista value={form.billing_address} onChange={onChange("billing_address")} placeholder="Via Roma 42, 20100 Milano (MI)" data-testid="billing-address-input" />
            </div>

            {/* iter v29 r275 — Avviso obbligatorietà SDI o PEC per fatturazione elettronica */}
            <div className="rounded-md bg-[#FFF8E6] border border-[#EBD9A8] p-3.5 text-[12.5px] text-[#7A5C18] leading-snug" data-testid="sdi-pec-notice">
              <strong>Importante per la fattura elettronica italiana:</strong> per ricevere la fattura tramite Sistema di Interscambio (SDI), devi inserire <strong>almeno uno</strong> tra:
              <ul className="mt-1 ml-4 list-disc">
                <li><strong>Codice SDI</strong> a 7 caratteri (il tuo gestionale di fatturazione te lo dà · es. <code>SUBM70N</code>, Aruba <code>KRRH6B9</code>, Fatture in Cloud <code>M5UXCR1</code>)</li>
                <li>oppure indirizzo <strong>PEC</strong> (es. <code>tuostudio@pec.it</code>)</li>
              </ul>
              Se non hai nessuno dei due, il codice destinatario sarà <code>0000000</code> e la fattura sarà recapitata nel tuo Cassetto Fiscale dell'Agenzia delle Entrate.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="overline block mb-2">Codice SDI</label>
                <input data-vista value={form.billing_sdi} onChange={onChange("billing_sdi")} placeholder="0000000" maxLength={7} data-testid="billing-sdi-input" />
                {form.billing_sdi && !/^[A-Z0-9]{7}$/i.test(form.billing_sdi.replace(/\s/g, "")) && (
                  <p className="text-[11px] text-[#C45A3E] mt-1">Esattamente 7 caratteri alfanumerici.</p>
                )}
              </div>
              <div>
                <label className="overline block mb-2">PEC</label>
                <input data-vista type="email" value={form.billing_pec} onChange={onChange("billing_pec")} placeholder="agenzia@pec.it" data-testid="billing-pec-input" />
                {form.billing_pec && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.billing_pec) && (
                  <p className="text-[11px] text-[#C45A3E] mt-1">Email PEC non valida.</p>
                )}
              </div>
              <div>
                <label className="overline block mb-2">Paese</label>
                <input data-vista value={form.billing_country} onChange={onChange("billing_country")} placeholder="Italia" data-testid="billing-country-input" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-[#E5E7E0]">
              <div className="flex items-center gap-2 text-sm text-[#6B6A65]">
                <Receipt size={16} /> Per emissione fatture su richiesta
              </div>
              <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="save-billing-btn">
                {saving ? "Salvataggio..." : "Salva dati fatturazione"}
              </button>
            </div>

            <div className="mt-6 text-xs text-[#6B6A65] border-t border-[#E5E7E0] pt-4">
              <p>
                🧾 Per richiedere fattura per un pagamento già effettuato, apri il form{" "}
                <Link to="/contatti?argomento=fattura" className="underline text-[#7C8B6E]">
                  Richiesta fattura
                </Link>{" "}
                indicando il riferimento del pagamento e i dati di cui sopra.
              </p>
            </div>
          </form>
        )}

        {/* ACCOUNT & SUBSCRIPTION TAB */}
        {tab === "account" && (
          <div className="mt-8 space-y-6" data-testid="account-section">
            {/* Account info */}
            <div className="card-flat p-8">
              <h2 className="font-editorial text-2xl">Account</h2>
              <div className="mt-5 space-y-3 text-sm">
                <Row label="Nome" value={user?.name} />
                <Row label="Email" value={user?.email} testid="account-email" />
                <Row label="Email verificata" value={user?.email_verified ? "Sì" : "No"} />
                <Row label="Metodo di accesso" value={user?.auth_provider === "google" ? "Google" : "Email + password"} />
              </div>
            </div>

            {/* iter v28 r254 — Password estensione Chrome */}
            <ExtensionPasswordCard user={user} />

            {/* Subscription / Plan */}
            <SubscriptionCard user={user} refresh={refresh} planInfo={planInfo} />

            {/* Utilizzo dettagliato del piano */}
            <UsageCard />

            {/* iter v28 r372 — User M1282: rimossa sezione "Supporto" e
                bottone "Contatta supporto" (no contatto pubblico). I 2
                bottoni utili (rivedi tour + riapri guide) restano in una
                card neutra "Esperienza". */}
            <div className="card-flat p-8">
              <span className="overline">Esperienza</span>
              <h3 className="font-editorial text-xl mt-2">Tour e guide</h3>
              <p className="text-sm text-[#6B6A65] mt-2">
                Puoi rivedere il tour iniziale o riaprire tutte le guide che hai chiuso in giro per l'app.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    try {
                      localStorage.removeItem("pronto_onboarding_v1");
                      localStorage.removeItem("pronto_onboarding_magico");
                      localStorage.removeItem("pronto_welcome_dismissed");
                    } catch (_) { /* noop */ }
                    nav("/dashboard?onboarding=1");
                  }}
                  className="btn-outline text-sm inline-flex"
                  data-testid="restart-onboarding-btn"
                >
                  <Sparkle size={16} /> Rivedi il tour iniziale
                </button>
                <button
                  onClick={() => {
                    const count = resetAllDismissedIntros();
                    window.dispatchEvent(new Event("pronto:intros-reset"));
                    resetAllHints();
                    if (count === 0) {
                      toast.message("Nessuna guida nascosta da riaprire");
                    } else {
                      toast.success(`${count} ${count === 1 ? "guida riaperta" : "guide riaperte"} su tutta l'app`);
                    }
                  }}
                  className="btn-outline text-sm inline-flex"
                  data-testid="reset-intros-btn"
                  title="Riapri tutte le spiegazioni e i tooltip che hai chiuso in giro per l'app"
                >
                  <Question size={16} /> Riapri tutte le guide
                </button>
              </div>
            </div>

            {/* Notifiche browser */}
            <NotificationsCard />

            {/* iter v28 r399 — User M1309: «i template facciamo che
                possiamo personalizzarli e modificarli». */}
            <WhatsAppTemplatesCard />

            {/* Logout / Danger zone */}
            <div className="card-flat p-8">
              <span className="overline">Sessione</span>
              <h3 className="font-editorial text-xl mt-2">Esci dall'account</h3>
              <p className="text-sm text-[#6B6A65] mt-2">
                Esci da Pronto su questo dispositivo.
              </p>
              <button onClick={onLogout} className="mt-4 inline-flex items-center gap-2 text-[#7C8B6E] hover:underline text-sm font-medium" data-testid="profile-logout-btn">
                <SignOut size={16} /> Esci
              </button>
            </div>

            {/* iter v32 r54 — User M1470: «Tutti gli annunci, tutto. Svuoti
                completamente il mio profilo da qualsiasi cosa». Sezione
                separata da Logout/Elimina-account: l'account resta vivo,
                slug + abbonamento + identità intatti, ma tutti i contenuti
                (annunci, foto, contatti, sessioni, kit-jobs) vengono
                cancellati definitivamente. */}
            <ResetContentZone />

            {/* Danger zone — GDPR account deletion */}
            <DangerZone />
          </div>
        )}
      </AppShell>
  );
}

// iter v28 r254 — Password per estensione Chrome (utenti Google-OAuth)
// iter v28 r255 — User M1175: dopo salvataggio la card non rifletteva lo
// stato ("Password impostata ✓") e chiedeva la "password attuale" che gli
// utenti Google non avevano mai messo. Refactor: stato visibile sempre,
// auto-refresh di has_local_password dopo set, form richiede password
// attuale SOLO se l'utente ne aveva già una.
function ExtensionPasswordCard({ user }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [currentPwd, setCurrentPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(null); // null = loading

  // Stato server-side per evitare confusione: dopo set torna a fetchare.
  const refreshStatus = async () => {
    try {
      const res = await api.get("/auth/me-extras");
      setHasPassword(!!res.data?.has_local_password);
    } catch {
      setHasPassword(user?.auth_provider !== "google");
    }
  };

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const titleVerb = hasPassword ? "Cambia" : "Imposta";

  const onSave = async () => {
    if (pwd.length < 8) { toast.error("La password deve essere di almeno 8 caratteri"); return; }
    if (!/[!@#$%^&*(),.?":{}|<>_+\-=/\\\[\]~`'§]/.test(pwd)) { toast.error("Aggiungi almeno un carattere speciale (es. !, ?, @)"); return; }
    if (pwd !== confirmPwd) { toast.error("Le due password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/set-password", {
        new_password: pwd,
        current_password: hasPassword ? currentPwd : undefined,
      });
      toast.success(hasPassword
        ? "Password aggiornata. Aggiorna l'estensione Chrome con la nuova."
        : "Password impostata. Ora puoi accedere all'estensione Chrome.");
      setPwd(""); setConfirmPwd(""); setCurrentPwd("");
      setOpen(false);
      await refreshStatus(); // riflette subito lo stato aggiornato
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-flat p-8" id="ext-password-card" data-testid="ext-password-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <span className="overline">Estensione Chrome</span>
          <h2 className="font-editorial text-2xl mt-1">Password per Pronto Publisher</h2>

          {/* Stato evidente */}
          {hasPassword === null ? (
            <div className="mt-3 text-sm text-[#9A968B]">Carico stato…</div>
          ) : hasPassword ? (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#F1F4ED] border border-[#7C8B6E]/30 text-[13px] text-[#3F3D38]" data-testid="ext-password-status-set">
              <span className="text-[#7C8B6E]">✓</span>
              <strong>Password impostata.</strong>
              <span className="text-[#6B6A65]">Usala per accedere all'estensione Chrome.</span>
            </div>
          ) : (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#FFF6E5] border border-[#F2D89E] text-[13px] text-[#8B6914]" data-testid="ext-password-status-missing">
              <span>⚠️</span>
              <strong>Non impostata.</strong>
              <span>Serve per usare l'estensione Chrome Pronto Publisher.</span>
            </div>
          )}

          <p className="text-sm text-[#6B6A65] mt-3 max-w-xl leading-relaxed">
            {user?.auth_provider === "google"
              ? <>Sei entrato con Google. Per l'estensione serve email + password (limite di Chrome): imposta qui una password locale da usare solo per l'estensione, il login Google continua a funzionare normalmente.</>
              : <>Puoi cambiare la password che usi ora per Pronto e l'estensione.</>}
          </p>
        </div>
        {!open && hasPassword !== null && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={hasPassword ? "btn-outline text-sm whitespace-nowrap" : "btn-primary text-sm whitespace-nowrap"}
            data-testid="ext-password-toggle"
          >
            {titleVerb} password
          </button>
        )}
      </div>

      {open && (
        <div className="mt-5 space-y-3 max-w-md">
          {hasPassword && (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">Password attuale</span>
              <input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                autoComplete="current-password"
                className="w-full mt-1 px-3 py-2 rounded-md border border-[#EAE7DF] text-sm"
                data-testid="ext-password-current"
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">Nuova password</span>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="new-password"
              placeholder="Min. 8 caratteri + 1 speciale"
              className="w-full mt-1 px-3 py-2 rounded-md border border-[#EAE7DF] text-sm"
              data-testid="ext-password-new"
            />
            <span className="text-[11px] text-[#9A968B]">Almeno 8 caratteri e un carattere speciale (es. <code>!</code>, <code>?</code>, <code>@</code>)</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">Conferma password</span>
            <input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              className="w-full mt-1 px-3 py-2 rounded-md border border-[#EAE7DF] text-sm"
              data-testid="ext-password-confirm"
            />
          </label>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onSave}
              disabled={loading}
              className="btn-primary text-sm disabled:opacity-50"
              data-testid="ext-password-save"
            >
              {loading ? "Salvataggio…" : `${titleVerb} password`}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setPwd(""); setConfirmPwd(""); setCurrentPwd(""); }}
              className="btn-outline text-sm"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResetContentZone() {
  const [open, setOpen] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [emailTyped, setEmailTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const expectedPhrase = "SVUOTA ACCOUNT";
  const expectedEmail = (user?.email || "").trim().toLowerCase();
  const ready = (
    confirmPhrase.trim().toUpperCase() === expectedPhrase
    && emailTyped.trim().toLowerCase() === expectedEmail
  );

  const onWipe = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const { data } = await api.post("/profile/wipe-content", {
        confirm_phrase: confirmPhrase,
        email_typed: emailTyped,
      });
      const total = data?.total_deleted ?? 0;
      toast.success(`Profilo svuotato · ${total} elementi rimossi`, { duration: 4500 });
      // Hard reload per ripulire ogni stato in cache
      setTimeout(() => { window.location.href = "/dashboard"; }, 1400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante lo svuotamento");
      setLoading(false);
    }
  };

  return (
    <div className="card-flat p-8 border border-[#E5E7E0]" data-testid="reset-content-zone">
      <span className="overline text-[#B5891E]">Svuotamento profilo</span>
      <h3 className="font-editorial text-xl mt-2">Svuota tutti i contenuti</h3>
      <p className="text-sm text-[#6B6A65] mt-2 leading-relaxed">
        Cancella definitivamente <strong className="text-[#1C1C1A]">annunci, foto, contatti, sopralluoghi, post social</strong> e tutti i materiali generati.
        Il tuo account, l&apos;abbonamento e il profilo agente <strong className="text-[#1C1C1A]">restano intatti</strong> — potrai ricominciare da zero senza dover rifare login.
        <br />
        <span className="text-[#A04040] font-medium">Operazione irreversibile.</span>
      </p>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 text-[#B5891E] hover:underline text-sm font-medium"
          data-testid="reset-content-open-btn"
        >
          Svuota il mio profilo →
        </button>
      ) : (
        <div className="mt-5 space-y-3 bg-[#FBF7EC] p-5 rounded-lg" data-testid="reset-content-form">
          <p className="text-xs text-[#6B6A65] italic">
            Doppia conferma per sicurezza. Scrivi entrambe le righe esattamente come indicato.
          </p>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">1) Scrivi <b>SVUOTA ACCOUNT</b> per confermare</span>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              className="mt-2 w-full px-4 py-2.5 border border-[#E5E7E0] focus:border-[#B5891E] outline-none rounded-sm"
              placeholder="SVUOTA ACCOUNT"
              autoCapitalize="characters"
              autoCorrect="off"
              data-testid="reset-content-phrase"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">2) Digita la tua email per conferma finale</span>
            <input
              type="email"
              value={emailTyped}
              onChange={(e) => setEmailTyped(e.target.value)}
              className="mt-2 w-full px-4 py-2.5 border border-[#E5E7E0] focus:border-[#B5891E] outline-none rounded-sm"
              placeholder={expectedEmail || "tuamail@..."}
              autoCapitalize="off"
              autoCorrect="off"
              data-testid="reset-content-email"
            />
          </label>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setOpen(false); setConfirmPhrase(""); setEmailTyped(""); }}
              className="text-sm text-[#6B6A65] underline"
              data-testid="reset-content-cancel"
            >
              Annulla
            </button>
            <button
              onClick={onWipe}
              disabled={loading || !ready}
              className="ml-auto bg-[#B5891E] hover:bg-[#8C6914] text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reset-content-confirm-btn"
            >
              {loading ? "Svuotamento..." : "Svuota tutto definitivamente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DangerZone() {
  const { user } = useAuth();
  const isOAuth = user?.auth_provider === "google" || user?.auth_provider === "apple";
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const onDelete = async () => {
    if (confirmText !== "ELIMINA") return toast.error("Scrivi ELIMINA in maiuscolo");
    setLoading(true);
    try {
      // iter 2026-06-12 — Apple 4.8 / Account Deletion: utenti Apple/Google
      // non hanno password, il backend skippa la verifica.
      const body = { confirm: "ELIMINA" };
      if (!isOAuth) body.password = password;
      await api.post("/auth/delete-account", body);
      toast.success("Account eliminato. Ci dispiace vederti andare.", { duration: 4000 });
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'eliminazione");
      setLoading(false);
    }
  };

  return (
    <div className="card-flat p-8 border border-[#E5E7E0]" data-testid="danger-zone">
      <span className="overline text-[#A04040]">Zona pericolo</span>
      <h3 className="font-editorial text-xl mt-2">Elimina account</h3>
      <p className="text-sm text-[#6B6A65] mt-2 leading-relaxed">
        Rimuovi definitivamente il tuo account e tutti i dati collegati:
        immobili, foto, schede, storico generazioni. <strong className="text-[#1C1C1A]">Operazione irreversibile</strong>.
      </p>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 text-[#A04040] hover:underline text-sm font-medium"
          data-testid="delete-account-open-btn"
        >
          Elimina il mio account →
        </button>
      ) : (
        <div className="mt-5 space-y-3 bg-[#F1F4ED] p-5 rounded-lg">
          {!isOAuth && (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">Conferma con la tua password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full px-4 py-2.5 border border-[#E5E7E0] focus:border-[#7C8B6E] outline-none"
                placeholder="••••••••"
                data-testid="delete-account-password"
              />
            </label>
          )}
          {isOAuth && (
            <p className="text-xs text-[#6B6A65] italic" data-testid="delete-account-oauth-notice">
              Accesso effettuato tramite {user?.auth_provider === "apple" ? "Apple" : "Google"}.
              Per confermare, scrivi <b>ELIMINA</b> qui sotto.
            </p>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A65]">Scrivi ELIMINA per confermare</span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mt-2 w-full px-4 py-2.5 border border-[#E5E7E0] focus:border-[#A04040] outline-none"
              placeholder="ELIMINA"
              data-testid="delete-account-confirm-text"
            />
          </label>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setOpen(false); setPassword(""); setConfirmText(""); }}
              className="text-sm text-[#6B6A65] underline"
              data-testid="delete-account-cancel"
            >
              Annulla
            </button>
            <button
              onClick={onDelete}
              disabled={loading || confirmText !== "ELIMINA"}
              className="ml-auto bg-[#A04040] hover:bg-[#7A2D2D] text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-50"
              data-testid="delete-account-confirm-btn"
            >
              {loading ? "Eliminazione..." : "Elimina definitivamente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#E5E7E0] last:border-0">
      <span className="text-[#6B6A65]">{label}</span>
      <span className="text-[#1C1C1A] font-medium" data-testid={testid}>{value || "—"}</span>
    </div>
  );
}


function SubscriptionCard({ user, refresh, planInfo }) {
  const [sub, setSub] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const openCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/payments/customer-portal", {
        return_url: window.location.origin + "/profile",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Portale non disponibile, riprova.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore apertura portale");
    } finally {
      setOpeningPortal(false);
    }
  };

  const creditsTotal = planInfo.credits;
  const creditsUsed = user?.ai_photos_used_this_month || 0;
  const creditsLeft = Math.max(0, creditsTotal - creditsUsed);

  useEffect(() => {
    api.get("/payments/me")
      .then(({ data }) => setSub(data))
      .catch(() => setSub(null));
  }, [user?.plan, user?.subscription_status]);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    } catch { return null; }
  };

  const onCancel = async () => {
    setCancelling(true);
    try {
      const { data: cancelRes } = await api.post("/payments/cancel-subscription");
      // iter v30 r098 — Mostriamo il messaggio personalizzato con DATA dal
      // backend ("...fino al 24 giugno 2026, poi tornerai...") invece del
      // generico "fino a fine ciclo".
      toast.success(cancelRes?.message || "Cancellazione registrata.");
      setConfirming(false);
      // Re-fetch subscription details
      const { data } = await api.get("/payments/me");
      setSub(data);
      refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore cancellazione");
    } finally {
      setCancelling(false);
    }
  };

  // iter v33 r106 · "promo" è il piano-ombra entitlement 60cr lancio,
  // NON un piano commerciale a pagamento. In UI si comporta come Free.
  const isPaid = user?.plan && user.plan !== "free" && user.plan !== "promo";
  const billingPeriod = sub?.subscription_billing_period === "annual" ? "annuale" : "mensile";
  const cancelRequested = !!sub?.cancel_requested_at;
  const inBeta = sub?.beta_mode;
  // iter v30 r098 — Trial 14 giorni Stripe: durante il trial l'utente NON
  // ha pagato nulla (€0). Il frontend deve mostrare "Periodo di prova ·
  // primo addebito il [data]" invece del fuorviante "Ultimo pagamento €29".
  const isTrial = !!sub?.is_trial;
  const trialEndIso = sub?.trial_end || sub?.subscription_current_period_end;
  const periodEndHuman = (() => {
    if (!trialEndIso) return "";
    try {
      const dt = new Date(trialEndIso);
      const months = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
                      "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
      return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
    } catch { return ""; }
  })();

  return (
    <div className="card-flat p-8" data-testid="subscription-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Abbonamento</span>
          <h2 className="font-editorial text-2xl mt-2">Piano {planInfo.name}</h2>
          <p className="text-sm text-[#6B6A65] mt-1">{planInfo.desc}</p>
        </div>
        {isReaderMode ? (
          // iter v30 r147 — Apple 3.1.1 fix: su iOS il CTA gestione/upgrade
          // piano DEVE aprire il paywall IAP interno (/dashboard/upgrade),
          // NON il sito web esterno (anti-steering). Su web invariato.
          <Link
            to="/dashboard/upgrade"
            className="btn-outline text-sm"
            data-testid="manage-plan-ios-btn"
          >
            {isPaid ? "Cambia piano" : "Passa a Pro"} <ArrowRight size={14} weight="bold" />
          </Link>
        ) : (
          <Link to="/pricing" className="btn-outline text-sm" data-testid="manage-plan-btn">
            {isPaid ? "Cambia piano" : "Gestisci piano"} <ArrowRight size={14} weight="bold" />
          </Link>
        )}
      </div>

      {/* iter 2026-06-12 — Apple 3.1.1 fix: Stripe Customer Portal nascosto
          su iOS native (accesso a gestione abbonamento esterno è considerato
          "accesso a contenuto pagato fuori dall'app"). Su web invariato. */}
      {isPaid && !inBeta && !isReaderMode && (
        <div className="mt-4">
          <button
            type="button"
            onClick={openCustomerPortal}
            disabled={openingPortal}
            className="btn-outline text-sm w-full sm:w-auto disabled:opacity-50"
            data-testid="open-customer-portal-btn"
          >
            {openingPortal ? "Apertura..." : "Gestisci abbonamento, carta e fatture"}
            <ArrowRight size={14} weight="bold" />
          </button>
          <p className="mt-2 text-[11px] text-[#9A968B]">
            Aggiorna i dati di fatturazione, cambia piano, scarica le fatture o disdici. Pagina sicura ospitata da Stripe.
          </p>
        </div>
      )}

      {/* Subscription metadata for paid plans */}
      {isPaid && sub && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px]" data-testid="subscription-meta">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Fatturazione</div>
            <div className="mt-1 text-[#1C1C1A] font-medium capitalize" data-testid="sub-billing-period">{billingPeriod}</div>
          </div>
          {/* iter v30 r098 — Durante il TRIAL mostriamo "Prova in corso · primo
              addebito il [data]" (€0 pagato) invece di "Ultimo pagamento €29"
              che era fuorviante. Dopo che il trial finisce e parte il pagamento
              vero, torniamo a mostrare data + importo classici. */}
          {isTrial ? (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Stato</div>
                <div className="mt-1 text-[#7C8B6E] font-semibold" data-testid="sub-trial-status">Prova in corso</div>
              </div>
              {periodEndHuman && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Primo addebito</div>
                  <div className="mt-1 text-[#1C1C1A] font-medium" data-testid="sub-trial-end">{periodEndHuman}</div>
                </div>
              )}
            </>
          ) : (
            <>
              {sub.subscription_paid_at && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Ultimo pagamento</div>
                  <div className="mt-1 text-[#1C1C1A] font-medium" data-testid="sub-last-paid">{fmtDate(sub.subscription_paid_at) || "—"}</div>
                </div>
              )}
              {sub.last_payment?.amount ? (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Importo</div>
                  <div className="mt-1 text-[#1C1C1A] font-medium" data-testid="sub-last-amount">€ {sub.last_payment.amount} <span className="text-[#9A968B]">{sub.last_payment.currency?.toUpperCase()}</span></div>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Credits bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[#1C1C1A] flex items-center gap-1.5">
            <Sparkle size={14} weight="fill" className="text-[#7C8B6E]" />
            Crediti {(user?.plan === "free" || user?.plan === "promo") ? "rimasti" : "questo mese"}
          </span>
          <span className="font-medium" data-testid="credits-counter">
            {creditsLeft} / {creditsTotal}
          </span>
        </div>
        <div className="h-2 bg-[#F1F4ED] overflow-hidden">
          <div
            className="h-full bg-[#7C8B6E] transition-all"
            style={{ width: `${creditsTotal > 0 ? (creditsLeft / creditsTotal) * 100 : 0}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-[#6B6A65]">
          {(user?.plan === "free" || user?.plan === "promo")
            ? "I crediti del piano Free si rinnovano ogni mese. Passa a Start o Pro per più crediti al mese."
            : "I crediti vengono rinnovati ogni mese."}
        </p>
      </div>

      {/* Cancellation block */}
      {isPaid && (
        <div className="mt-6 pt-6 border-t border-[#E5E7E0]" data-testid="cancellation-block">
          {cancelRequested ? (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded text-[12.5px] text-amber-900" data-testid="cancellation-pending">
              <strong>Cancellazione registrata il {fmtDate(sub.cancel_requested_at)}.</strong>
              {/* iter v30 r098 — Mostriamo la DATA esplicita di fine accesso
                  così l'utente sa fino a quando può continuare a usare il
                  piano. Se in trial, sottolineiamo che non ci sarà nessun
                  addebito. */}
              <span className="block mt-1">
                {periodEndHuman ? (
                  <>
                    {isTrial
                      ? <>Resti nel periodo di prova fino al <strong>{periodEndHuman}</strong>, poi tornerai automaticamente al piano Free. <strong>Nessun addebito.</strong></>
                      : <>Il piano resta attivo fino al <strong>{periodEndHuman}</strong>, poi tornerai automaticamente al piano Free.</>
                    }
                  </>
                ) : (
                  <>Il piano resta attivo fino a fine ciclo.</>
                )}
                <br/>Per annullare la richiesta, apri il form <Link to="/contatti?argomento=disdetta" className="underline">Contattaci</Link>.
              </span>
            </div>
          ) : !confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-[12.5px] text-[#6B6A65] hover:text-[#A04040] underline underline-offset-2"
              data-testid="cancel-subscription-open-btn"
            >
              {inBeta
                ? "Disdici l'abbonamento (al lancio)"
                : "Disdici l'abbonamento"}
            </button>
          ) : (
            <div className="bg-[#F1F4ED] p-4 rounded space-y-3" data-testid="cancel-subscription-confirm">
              <p className="text-[13px] text-[#1C1C1A] leading-relaxed">
                Vuoi davvero cancellare il piano <strong>{planInfo.name}</strong>?
                {inBeta ? (
                  <span className="block mt-1 text-[#6B6A65]">La disdetta sarà valida quando partiranno i pagamenti — niente addebiti a sorpresa.</span>
                ) : isTrial && periodEndHuman ? (
                  <span className="block mt-1 text-[#6B6A65]">Resti nel periodo di prova fino al <strong>{periodEndHuman}</strong>, poi tornerai automaticamente al piano Free. <strong>Non ti addebiteremo nulla.</strong></span>
                ) : periodEndHuman ? (
                  <span className="block mt-1 text-[#6B6A65]">Il piano resta attivo fino al <strong>{periodEndHuman}</strong>, poi tornerai automaticamente al piano Free.</span>
                ) : (
                  <span className="block mt-1 text-[#6B6A65]">Il piano resta attivo fino a fine ciclo, poi tornerai automaticamente al piano Free.</span>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="text-[13px] text-[#6B6A65] underline"
                  data-testid="cancel-subscription-back"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={cancelling}
                  className="ml-auto bg-[#A04040] hover:bg-[#7A2D2D] text-white px-4 py-2 rounded-sm text-[13px] font-medium disabled:opacity-50"
                  data-testid="cancel-subscription-confirm-btn"
                >
                  {cancelling ? "Invio..." : "Conferma disdetta"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function LogoUploader({ value, onChange, onRefresh, userId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Resolve the preview source: absolute URL as-is, internal path via the public endpoint.
  // iter v28 r455 — Stesso fix dell'AgentPhotoUploader: useMemo invece di IIFE
  // inline, così `?ts=` cambia SOLO al cambio di value/userId.
  const previewSrc = useMemo(() => {
    if (!value) return null;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    if (userId) return `${API}/public/agency/${userId}/logo?ts=${Date.now()}`;
    return null;
  }, [value, userId]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il file è troppo grande (max 2 MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/agency/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.agency_logo_url || "");
      toast.success("Logo caricato");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemove = () => {
    // iOS PWA blocks window.confirm in standalone mode → use sonner action toast
    toast(
      "Rimuovere il logo dell'agenzia?",
      {
        action: {
          label: "Rimuovi",
          onClick: async () => {
            try {
              await api.delete("/profile/agency/logo");
              onChange("");
              toast.success("Logo rimosso");
              onRefresh?.();
            } catch { toast.error("Errore rimozione"); }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  return (
    <div className="flex items-center gap-4" data-testid="logo-uploader">
      <div className="w-24 h-24 flex-shrink-0 border border-[#E5E7E0] bg-[#F7F7F3] flex items-center justify-center overflow-hidden">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Logo agenzia"
            className="w-full h-full object-contain p-2"
            data-testid="logo-preview"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <Buildings size={28} weight="light" className="text-[#C3C2BD]" />
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={onFile}
          className="hidden"
          data-testid="logo-file-input"
        />
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="btn-outline text-sm disabled:opacity-60"
          data-testid="logo-upload-btn"
        >
          <UploadSimple size={14} weight="bold" />
          {uploading ? "Caricamento..." : value ? "Sostituisci logo" : "Carica logo"}
        </button>
        {value && (
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-[#6B6A65] hover:text-[#B8624A] flex items-center gap-1"
            data-testid="logo-remove-btn"
          >
            <Trash size={14} /> Rimuovi
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * AgentPhotoUploader — iter 309 r9.
 * Carica una foto personale dell'agente che SOSTITUISCE la foto Google su
 * PDF stampabili, email, e materiali pubblici. La foto Google `picture` non viene
 * mai esposta in nessun output user-facing.
 */
function AgentPhotoUploader({ value, onChange, onRefresh, userId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // iter v28 r455 — User M1371: «nel profilo utente non si carica la foto
  // profilo». Bug: `?ts=${Date.now()}` veniva ricalcolato ad ogni render
  // del Profile (form typing → parent setState → child re-render → src cambia
  // ogni millisecondo → l'<img> non finisce mai di caricarsi → si vede solo
  // l'alt text "Foto agente"). Fix: stabilizzo `?ts` con useMemo legato a
  // [value, userId], così cambia SOLO quando l'agente carica/rimuove la foto.
  const previewSrc = useMemo(() => {
    if (!value) return null;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    if (userId) return `${API}/public/agency/${userId}/agent-photo?ts=${Date.now()}`;
    return null;
  }, [value, userId]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Il file è troppo grande (max 4 MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/agent/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.agent_photo_url || "");
      toast.success("Foto caricata");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemove = () => {
    toast("Rimuovere la tua foto?", {
      action: {
        label: "Rimuovi",
        onClick: async () => {
          try {
            await api.delete("/profile/agent/photo");
            onChange("");
            toast.success("Foto rimossa");
            onRefresh?.();
          } catch { toast.error("Errore rimozione"); }
        },
      },
      cancel: { label: "Annulla", onClick: () => {} },
      duration: 8000,
    });
  };

  return (
    <div className="flex items-center gap-4" data-testid="agent-photo-uploader">
      <div className="w-24 h-24 flex-shrink-0 rounded-full border border-[#E5E7E0] bg-[#F7F7F3] flex items-center justify-center overflow-hidden">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Foto agente"
            className="w-full h-full object-cover"
            data-testid="agent-photo-preview"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span className="text-[#C3C2BD] text-xs font-bold tracking-wider">FOTO</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onFile}
          className="hidden"
          data-testid="agent-photo-file-input"
        />
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="btn-outline text-sm disabled:opacity-60"
          data-testid="agent-photo-upload-btn"
        >
          <UploadSimple size={14} weight="bold" />
          {uploading ? "Caricamento..." : value ? "Sostituisci foto" : "Carica foto"}
        </button>
        {value && (
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-[#6B6A65] hover:text-[#B8624A] flex items-center gap-1"
            data-testid="agent-photo-remove-btn"
          >
            <Trash size={14} /> Rimuovi
          </button>
        )}
      </div>
    </div>
  );
}

function NotificationsCard() {
  const { isSupported, status, loading, error, subscribe, unsubscribe, sendTest } = usePushNotifications();
  const isOn = status === "subscribed";
  // iter v33 r41 — Utente ha chiesto conferma che le notifiche iOS arrivano
  // anche ad app chiusa. Sull'app Capacitor `isSupported` è false (WKWebView
  // non espone Service Worker/PushManager) → prima la card faceva early-
  // return con "browser non supporta". Adesso, se siamo dentro l'app iOS
  // nativa, mostriamo comunque la card con il pulsante di test APNs +
  // indicatore dispositivi + preferenze agenda.
  const [agendaPref, setAgendaPref] = useState(true);
  const [reminderMin, setReminderMin] = useState(30);
  const [homeAlertsPref, setHomeAlertsPref] = useState(true);
  const [prefLoading, setPrefLoading] = useState(false);
  // iter v32 r49c — User M1450: indicatore «Notifiche attive su N device»
  // + bottone "Manda push di prova" con feedback dettagliato. Fa GET
  // /push/devices per sapere quanti device (web+iOS) sono raggiungibili.
  const [devices, setDevices] = useState(null);  // null | {web_count, ios_count, apns_configured}
  const [testing, setTesting] = useState(false);

  const refreshDevices = async () => {
    try {
      const { data } = await api.get("/push/devices");
      setDevices(data);
    } catch {
      // silenzioso: l'endpoint è opzionale, la card resta funzionale
    }
  };

  useEffect(() => {
    refreshDevices();
  }, [isOn]);

  useEffect(() => {
    // iter v28 r389: home_alerts_enabled non dipende dalle push browser,
    // quindi lo carichiamo SEMPRE (anche se l'utente non è subscribed).
    api.get("/push/agenda-pref")
      .then(({ data }) => {
        setAgendaPref(!!data.enabled);
        if (data.reminder_minutes) setReminderMin(data.reminder_minutes);
        setHomeAlertsPref(data.home_alerts_enabled !== false);
      })
      .catch(() => {});
  }, [isOn]);

  const toggleHomeAlertsPref = async () => {
    const next = !homeAlertsPref;
    setPrefLoading(true);
    setHomeAlertsPref(next);
    try {
      await api.patch("/push/agenda-pref", { home_alerts_enabled: next });
      toast.success(next ? "Banner sulla home attivati" : "Banner sulla home disattivati");
    } catch (e) {
      setHomeAlertsPref(!next);
      toast.error(e?.response?.data?.detail || "Errore aggiornamento preferenza");
    } finally {
      setPrefLoading(false);
    }
  };

  const toggleAgendaPref = async () => {
    const next = !agendaPref;
    setPrefLoading(true);
    setAgendaPref(next); // optimistic
    try {
      await api.patch("/push/agenda-pref", { enabled: next });
      toast.success(next ? "Notifiche agenda attivate" : "Notifiche agenda disattivate");
    } catch (e) {
      setAgendaPref(!next); // rollback
      toast.error(e?.response?.data?.detail || "Errore aggiornamento preferenza");
    } finally {
      setPrefLoading(false);
    }
  };

  const updateReminderMin = async (minutes) => {
    const prev = reminderMin;
    setPrefLoading(true);
    setReminderMin(minutes); // optimistic
    try {
      await api.patch("/push/agenda-pref", { reminder_minutes: minutes });
      toast.success(`Promemoria impostato a ${minutes} min prima`);
    } catch (e) {
      setReminderMin(prev); // rollback
      toast.error(e?.response?.data?.detail || "Errore aggiornamento preferenza");
    } finally {
      setPrefLoading(false);
    }
  };

  // iter v33 r41 — Su iOS nativo mostriamo comunque la card: le notifiche
  // arrivano via APNs, non via Web Push, quindi il "browser non supporta"
  // è fuorviante. Il fallback resta solo per browser desktop legacy.
  if (!isSupported && !isNative) {
    return (
      <div className="card-flat p-8" data-testid="notifications-card">
        <span className="overline">Notifiche</span>
        <h3 className="font-editorial text-xl mt-2 flex items-center gap-2">
          <BellSlash size={20} className="text-[#9A968B]" /> Notifiche browser
        </h3>
        <p className="text-sm text-[#6B6A65] mt-2">
          Il tuo browser non supporta le notifiche push. Su iPhone, prima aggiungi Pronto alla schermata Home, poi torna qui.
        </p>
      </div>
    );
  }

  const onTest = async () => {
    setTesting(true);
    try {
      const r = await sendTest();
      const n = r?.sent_to_devices || 0;
      if (n > 0) {
        toast.success(`Notifica inviata a ${n} dispositiv${n === 1 ? "o" : "i"}`);
      } else {
        // iter v33 r42 — Con 16 device stale su prod era impossibile capire
        // il motivo. Adesso il backend ritorna `apns.failures[]` con le
        // ragioni Apple per token: mostriamo il primo motivo aggregato +
        // suggerimento operativo (i token vecchi vengono già ripuliti
        // automaticamente lato server, quindi al prossimo tentativo il
        // conteggio dispositivi si abbassa).
        const iosN = devices?.ios_count || 0;
        const webN = devices?.web_count || 0;
        const apnsCfg = devices?.apns_configured;
        const failures = r?.apns?.failures || [];
        const apnsTotal = r?.apns?.total || 0;
        // Aggrega i motivi
        const reasons = {};
        failures.forEach((f) => { reasons[f.reason] = (reasons[f.reason] || 0) + 1; });
        const topReason = Object.entries(reasons).sort((a, b) => b[1] - a[1])[0];

        if (iosN === 0 && webN === 0) {
          toast.error("Nessun dispositivo registrato. Apri Pronto su iPhone o attiva le notifiche browser.");
        } else if (iosN > 0 && !apnsCfg) {
          toast.error("App iOS registrata ma APNs non ancora configurato sul server.");
        } else if (topReason) {
          const [reason, count] = topReason;
          // Traduci i codici Apple in italiano
          const REASON_IT = {
            Unregistered: "l'app è stata disinstallata o reinstallata",
            BadDeviceToken: "token invalido (ambiente sandbox vs produzione)",
            DeviceTokenNotForTopic: "token appartiene a un'altra versione dell'app",
            TopicDisallowed: "bundle id non autorizzato dalla chiave APNs",
            MissingDeviceToken: "token vuoto in database",
            BadTopic: "APNS_TOPIC malformato",
            InvalidProviderToken: "chiave APNs scaduta lato server",
          };
          const human = REASON_IT[reason] || reason;
          toast.error(
            `Nessuna notifica recapitata (${count}/${apnsTotal} device: ${human}). I token vecchi verranno ripuliti automaticamente.`,
            { duration: 9000 },
          );
        } else {
          toast.error("Notifica non recapitata. Il device potrebbe avere il permesso revocato.");
        }
      }
      // refresh dei conteggi: la send_push pulisce gli endpoint scaduti
      refreshDevices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setTesting(false);
    }
  };

  // iter v33 r41 — Su app iOS nativa, "attive" significa avere almeno 1
  // push token APNs registrato. Web Push non c'entra.
  const iosActive = isNative && (devices?.ios_count || 0) > 0;
  const anyPushActive = isOn || iosActive;

  return (
    <div className="card-flat p-8" data-testid="notifications-card">
      <span className="overline">Notifiche</span>
      <h3 className="font-editorial text-xl mt-2 flex items-center gap-2">
        {anyPushActive
          ? <><BellRinging size={20} weight="fill" className="text-[#7C8B6E]" /> Notifiche attive</>
          : <><Bell size={20} className="text-[#6B6A65]" /> {isNative ? "Notifiche iOS" : "Notifiche browser"}</>
        }
      </h3>
      <p className="text-sm text-[#6B6A65] mt-2">
        Ricevi sul telefono <strong>nuove richieste</strong>, <strong>richieste di stima</strong> e <strong>promemoria agenda</strong> — anche se Pronto è chiusa.
      </p>

      {/* iter v32 r49c — Indicatore device raggiungibili (iOS + browser).
          Si vede sempre, anche prima di attivare le notifiche browser, così
          se hai solo l'app iOS sai che è già a posto e non devi fare nulla. */}
      {devices && (devices.total > 0 || devices.ios_count > 0 || devices.web_count > 0) ? (
        <div
          className="mt-4 flex flex-wrap items-center gap-2 text-[12.5px]"
          data-testid="push-devices-indicator"
        >
          {devices.ios_count > 0 && (
            <span
              className="inline-flex items-center gap-1.5 bg-[#F1F4ED] border border-[#7C8B6E]/30 text-[#5C6B4E] font-semibold px-2.5 py-1 rounded-full"
              data-testid="push-devices-ios"
            >
              <DeviceMobile size={13} weight="fill" />
              iOS · {devices.ios_count} {devices.ios_count === 1 ? "dispositivo" : "dispositivi"}
            </span>
          )}
          {devices.web_count > 0 && (
            <span
              className="inline-flex items-center gap-1.5 bg-[#F1F4ED] border border-[#7C8B6E]/30 text-[#5C6B4E] font-semibold px-2.5 py-1 rounded-full"
              data-testid="push-devices-web"
            >
              <Browser size={13} weight="bold" />
              Browser · {devices.web_count} {devices.web_count === 1 ? "dispositivo" : "dispositivi"}
            </span>
          )}
        </div>
      ) : devices && devices.total === 0 ? (
        <p
          className="mt-4 text-[12.5px] text-[#9A968B] italic"
          data-testid="push-devices-empty"
        >
          {isNative
            ? "Il permesso notifiche è stato negato. Vai in Impostazioni iOS → Pronto → Notifiche → Consenti, poi riapri Pronto."
            : "Nessun dispositivo registrato. Apri Pronto su iPhone per registrare la app, o attiva le notifiche browser qui sotto."
          }
        </p>
      ) : null}

      {error && (
        <p className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 p-2.5 rounded">{error}</p>
      )}

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        {isNative ? (
          // iter v33 r41 — App iOS nativa: le notifiche APNs si attivano
          // in automatico al primo avvio (permesso iOS). Qui mostriamo
          // solo il pulsante di test, che funziona quando c'è almeno un
          // device registrato. Nessun subscribe/unsubscribe browser.
          <button
            type="button"
            onClick={onTest}
            disabled={testing || iosActive === false}
            className="btn-primary text-sm"
            data-testid="test-notification-btn"
            title={iosActive ? "Manda una vera push sul tuo iPhone adesso" : "Prima devi consentire le notifiche in Impostazioni iOS"}
          >
            <BellRinging size={16} weight="fill" /> {testing ? "Invio…" : "Invia notifica di prova"}
          </button>
        ) : !isOn ? (
          <>
            <button
              type="button"
              onClick={subscribe}
              disabled={loading || status === "denied"}
              className="btn-primary"
              data-testid="enable-notifications-btn"
            >
              <Bell size={16} weight="fill" /> {loading ? "Attivo…" : "Attiva notifiche"}
            </button>
            {/* iter v32 r49c — Se c'è almeno un device iOS registrato,
                permetti comunque il test push: l'utente potrebbe non aver
                attivato il browser ma avere già l'app iOS funzionante. */}
            {devices?.ios_count > 0 && (
              <button
                type="button"
                onClick={onTest}
                disabled={testing}
                className="btn-outline text-sm"
                data-testid="test-notification-btn"
              >
                <BellRinging size={14} weight="bold" /> {testing ? "Invio…" : "Invia notifica di prova"}
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onTest}
              disabled={loading || testing}
              className="btn-outline text-sm"
              data-testid="test-notification-btn"
            >
              <BellRinging size={14} weight="bold" /> {testing ? "Invio…" : "Invia notifica di prova"}
            </button>
            <button
              type="button"
              onClick={unsubscribe}
              disabled={loading}
              className="text-sm text-[#6B6A65] hover:text-[#B8624A]"
              data-testid="disable-notifications-btn"
            >
              Disattiva
            </button>
          </>
        )}
      </div>

      {/* Sottopreferenza: agenda — visibile se browser attivo O se app iOS
          nativa. iter v33 r41: prima era solo `isOn` → sull'app iOS non si
          vedevano i toggle promemoria pur avendo APNs attivo. */}
      {anyPushActive && (
        <div className="mt-6 pt-5 border-t border-[#E5E1D5]" data-testid="agenda-pref-block">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#1C1C1A]">Promemoria agenda</div>
              <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-relaxed">
                Briefing del mattino alle <strong>08:00</strong> con le cose da fare oggi · <strong>{reminderMin} min</strong> prima di ogni appuntamento · recap serale alle <strong>18:00</strong> solo se hai cose ancora aperte. Rispetta il weekend.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={agendaPref}
              onClick={toggleAgendaPref}
              disabled={prefLoading}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${agendaPref ? "bg-[#7C8B6E]" : "bg-[#D5D2C8]"} disabled:opacity-60`}
              data-testid="agenda-pref-toggle"
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${agendaPref ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          {/* Lead-time selector — only when agenda notifications are ON */}
          {agendaPref && (
            <div className="mt-4" data-testid="reminder-minutes-block">
              <div className="text-[11.5px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold mb-2">
                Anticipo promemoria
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[5, 15, 30, 60, 120].map((min) => (
                  <button
                    key={min}
                    type="button"
                    onClick={() => updateReminderMin(min)}
                    disabled={prefLoading || reminderMin === min}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
                      reminderMin === min
                        ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                        : "bg-white text-[#1C1C1A] border-[#E5E1D5] hover:border-[#7C8B6E]"
                    } disabled:cursor-not-allowed`}
                    data-testid={`reminder-min-${min}`}
                  >
                    {min < 60 ? `${min} min prima` : (min === 60 ? "1 ora prima" : `${min/60} ore prima`)}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#9A968B] mt-2 italic">
                Vale sia per le notifiche push che per gli avvisi del calendario sincronizzato.
              </p>
            </div>
          )}
        </div>
      )}

      {/* iter v28 r389 — Toggle indipendente per i banner sulla home
          (compleanni clienti + festività + recap agenda di oggi).
          Visibile sempre, non dipende dalle push browser. */}
      <div className="mt-6 pt-5 border-t border-[#E5E1D5]" data-testid="home-alerts-pref-block">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#1C1C1A]">Avvisi sulla home</div>
            <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-relaxed">
              Banner in cima alla homepage con <strong>compleanni dei clienti</strong>,
              <strong> festività imminenti</strong> e <strong>recap agenda di oggi</strong>.
              Disattivalo se preferisci una home pulita.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={homeAlertsPref}
            onClick={toggleHomeAlertsPref}
            disabled={prefLoading}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${homeAlertsPref ? "bg-[#7C8B6E]" : "bg-[#D5D2C8]"} disabled:opacity-60`}
            data-testid="home-alerts-pref-toggle"
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${homeAlertsPref ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>
      </div>

      {status === "denied" && (
        <p className="mt-3 text-xs text-[#9A968B]">
          Le notifiche sono <strong>bloccate</strong> dal browser. Sbloccale dalle impostazioni del sito (icona vicino all'URL → Notifiche → Consenti).
        </p>
      )}
    </div>
  );
}

function WhatsAppTemplatesCard() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // key of currently editing template
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // iter v30 r099 — User M1750 bugfix: il backend ritornava `icon` come
  // STRINGA (nome icona Phosphor: "calculator", "tree", ecc.) e il
  // frontend la renderizzava letteralmente come testo (vedi screenshot
  // "calculator Stima gratuita casa"). Mappa qui i nomi alle emoji
  // appropriate per occasione, così l'utente vede un'icona piccola e
  // significativa accanto a ogni template, NON una parola inglese.
  const ICON_MAP = {
    calculator: "🧮", tree: "🎄", fireworks: "🎆", heart: "💝",
    flower: "🌸", user: "👨", egg: "🥚", flag: "🇮🇹", sun: "☀️",
    cake: "🎂", house: "🏡", handshake: "🤝", wave: "👋",
    chat: "💬", megaphone: "📣",
  };
  const iconFor = (raw) => {
    if (!raw) return "💬";
    // Già emoji (es. backward-compat con vecchie config) → usa così com'è
    if (raw.length <= 4 && !/^[a-z_]+$/i.test(raw)) return raw;
    return ICON_MAP[raw] || "💬";
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/bulk-greetings/templates");
      setTemplates(data.templates || []);
      setVariables(data.variables || []);
    } catch {
      toast.error("Impossibile caricare i template");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditing(t.key);
    setDraft(t.custom_text || t.default_text || "");
  };

  const save = async (key) => {
    setSaving(true);
    try {
      await api.put("/contacts/bulk-greetings/templates", { occasion: key, text: draft });
      toast.success("Template salvato");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Ripristinare il messaggio di default Pronto?")) return;
    setSaving(true);
    try {
      await api.put("/contacts/bulk-greetings/templates", { occasion: key, text: "" });
      toast.success("Template ripristinato");
      setEditing(null);
      await load();
    } catch {
      toast.error("Ripristino fallito");
    } finally {
      setSaving(false);
    }
  };

  // iter v30 r099 — User M1750: «occupano troppo spazio, fai che si
  // possono chiudere». Tutta la sezione ora è collassata di default
  // (<details>). Ogni singolo template è collassato a sua volta: si vede
  // solo emoji + label, click per espandere e leggere/modificare il testo.
  // Conteggio personalizzati visibile nel summary così l'utente sa al
  // volo quanti template ha già toccato.
  const customizedCount = templates.filter((t) => t.is_customized).length;
  const totalCount = templates.length;

  return (
    <details className="card-flat" data-testid="whatsapp-templates-card">
      <summary className="p-6 cursor-pointer list-none flex items-start justify-between gap-4 hover:bg-[#FAF8F3] transition-colors rounded-2xl">
        <div className="flex-1 min-w-0">
          <span className="overline">Messaggi WhatsApp</span>
          <h3 className="font-editorial text-xl mt-2 flex items-center gap-2">
            I tuoi template
            <span className="text-[#9A968B] text-[14px] font-normal">
              · {customizedCount}/{totalCount} personalizzati
            </span>
          </h3>
          <p className="text-sm text-[#6B6A65] mt-2 leading-relaxed">
            Personalizza i messaggi che Pronto manda ai tuoi clienti per <strong>compleanni</strong>,
            <strong> festività</strong>, <strong>ringraziamenti dopo rogito</strong> e altre occasioni.
          </p>
        </div>
        <span className="shrink-0 mt-2 inline-flex items-center gap-1 text-[12px] text-[#7C8B6E] font-semibold">
          <span className="details-chevron transition-transform">▸</span>
          <span className="hidden sm:inline">Apri</span>
        </span>
      </summary>

      <div className="px-6 pb-6 border-t border-[#EAE7DF]">
        {variables.length > 0 && (
          <details className="mt-4 group">
            <summary className="cursor-pointer text-[12.5px] font-semibold text-[#7C8B6E] hover:underline list-none inline-flex items-center gap-1">
              <span className="transition-transform group-open:rotate-90">▸</span>
              Variabili che puoi usare nei messaggi
            </summary>
            <div className="mt-2 p-3 bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-lg space-y-1.5">
              {variables.map((v) => (
                <div key={v.name} className="text-[12px] text-[#1C1C1A] flex items-start gap-2">
                  <code className="px-1.5 py-0.5 bg-white text-[#7C8B6E] font-mono text-[11px] rounded border border-[#7C8B6E]/30 shrink-0">{v.name}</code>
                  <span className="text-[#6B6A65]">{v.desc}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {loading ? (
          <div className="mt-4 text-sm text-[#9A968B]">Caricamento…</div>
        ) : (
          <div className="mt-4 space-y-2">
            {templates.map((t) => (
              <details
                key={t.key}
                className="bg-white border border-[#E5E1D5] rounded-lg overflow-hidden group/tpl"
                open={editing === t.key}
                data-testid={`tpl-${t.key}`}
              >
                <summary
                  className="px-3.5 py-3 cursor-pointer list-none flex items-center gap-2.5 hover:bg-[#FAF8F3] transition-colors"
                  onClick={(e) => {
                    // Se sto modificando, non chiudere
                    if (editing === t.key) { e.preventDefault(); }
                  }}
                >
                  <div className="text-[18px] shrink-0 leading-none">{iconFor(t.icon)}</div>
                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <div className="text-[13.5px] font-semibold text-[#1C1C1A]">{t.label}</div>
                    {t.is_customized && (
                      <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F1F4ED] text-[#5C6B4E] px-1.5 py-0.5 rounded">Personalizzato</span>
                    )}
                  </div>
                  <span className="shrink-0 text-[11px] text-[#9A968B] transition-transform group-open/tpl:rotate-90">▸</span>
                </summary>
                <div className="px-3.5 pb-3.5 pt-1 border-t border-[#F1ECE2]">
                  {editing === t.key ? (
                    <div className="mt-2">
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={5}
                        maxLength={1000}
                        className="w-full text-[13px] text-[#1C1C1A] bg-white border border-[#E5E1D5] focus:border-[#7C8B6E] focus:outline-none rounded-lg p-2.5 resize-none"
                        placeholder={t.default_text}
                        data-testid={`tpl-textarea-${t.key}`}
                      />
                      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[11px] text-[#9A968B]">{draft.length}/1000 caratteri</div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setEditing(null)}
                            className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A65] hover:bg-[#F5F4F0]"
                            data-testid={`tpl-cancel-${t.key}`}
                          >Annulla</button>
                          {t.is_customized && (
                            <button
                              onClick={() => reset(t.key)}
                              disabled={saving}
                              className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A65] hover:bg-[#F5F4F0] disabled:opacity-50"
                              data-testid={`tpl-reset-${t.key}`}
                            >Ripristina default</button>
                          )}
                          <button
                            onClick={() => save(t.key)}
                            disabled={saving || !draft.trim()}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-[#1C1C1A] text-white hover:bg-black disabled:opacity-50"
                            data-testid={`tpl-save-${t.key}`}
                          >{saving ? "Salvo…" : "Salva"}</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mt-2 text-[12.5px] text-[#6B6A65] whitespace-pre-wrap leading-snug">
                        {t.custom_text || t.default_text}
                      </div>
                      <button
                        onClick={() => startEdit(t)}
                        className="mt-2 text-[12px] font-semibold text-[#7C8B6E] hover:underline"
                        data-testid={`tpl-edit-${t.key}`}
                      >
                        {t.is_customized ? "Modifica" : "Personalizza"} →
                      </button>
                    </>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

function UsageCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/usage")
      .then(({ data }) => setUsage(data))
      .catch(() => setUsage(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card-flat p-8" data-testid="usage-card-loading">
        <div className="h-4 w-24 bg-[#F1F4ED] rounded animate-pulse" />
        <div className="mt-3 h-3 w-2/3 bg-[#F1F4ED] rounded animate-pulse" />
      </div>
    );
  }
  if (!usage) return null;

  const fmt = (n, mx, unl) => unl ? "Illimitato" : `${n} di ${mx}`;
  const resetText = (() => {
    if (!usage.ai_photos.reset_at) return null;
    const d = new Date(usage.ai_photos.reset_at);
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  })();

  return (
    <div className="card-flat p-8" data-testid="usage-card">
      <span className="overline">Utilizzo del piano</span>
      <h3 className="font-editorial text-2xl mt-2">A che punto sei.</h3>
      <p className="text-sm text-[#6B6A65] mt-1">
        Quanto stai sfruttando il piano <strong>{usage.plan_name}</strong>{usage.is_trial ? " (prova gratuita)" : ""}.
      </p>

      <div className="mt-6 space-y-5">
        {/* Properties */}
        <UsageRow
          icon={House}
          label="Immobili attivi"
          value={fmt(usage.properties.used, usage.properties.max, usage.properties.unlimited)}
          pct={usage.properties.unlimited ? 0 : usage.properties.pct}
          warn={!usage.properties.unlimited && usage.properties.pct >= 80}
          subtitle={usage.properties.unlimited
            ? "Nessun limite, gestisci quanti immobili vuoi."
            : usage.properties.pct >= 100
            ? "Limite raggiunto. Per gestire altri immobili, passa al piano Pro."
            : usage.properties.pct >= 80
            ? "Sei vicino al limite del piano. Considera l'upgrade."
            : "Spazio disponibile per nuovi immobili."}
        />

        {/* AI photos */}
        <UsageRow
          icon={Camera}
          label="Foto migliorate questo mese"
          value={fmt(usage.ai_photos.used, usage.ai_photos.max, usage.ai_photos.unlimited)}
          pct={usage.ai_photos.unlimited ? 0 : usage.ai_photos.pct}
          warn={!usage.ai_photos.unlimited && usage.ai_photos.pct >= 80}
          subtitle={usage.ai_photos.unlimited
            ? "Quota foto inclusa nel piano · ogni foto migliorata = 1 credito smart."
            : usage.ai_photos.pct >= 100
            ? `Limite raggiunto. ${usage.ai_photos.extra_eur ? `Foto extra a €${usage.ai_photos.extra_eur}/cad.` : "Compra crediti aggiuntivi o passa a Pro."}`
            : resetText
            ? `Si rinnovano automaticamente il ${resetText}.`
            : "Si rinnovano il primo del mese."}
        />

        {/* Storage info */}
        <UsageRow
          icon={HardDrives}
          label="Foto archiviate"
          value={`${usage.storage.photos_count} · ~${usage.storage.estimated_mb} MB`}
          pct={0}
          subtitle="Lo spazio è sempre incluso, nessun limite su quante foto puoi conservare."
        />
      </div>
    </div>
  );
}

function UsageRow({ icon: Icon, label, value, pct, subtitle, warn }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-[13px] text-[#1C1C1A]">
          <Icon size={15} className="text-[#7C8B6E]" weight="regular" />
          <strong className="font-medium">{label}</strong>
        </span>
        <span className={`text-[13px] font-semibold ${warn ? "text-[#B8624A]" : "text-[#1C1C1A]"}`}>{value}</span>
      </div>
      {pct > 0 && (
        <div className="mt-2 h-1.5 bg-[#F1F4ED] rounded-full overflow-hidden">
          <div
            className={`h-full transition-all rounded-full ${warn ? "bg-[#B8624A]" : "bg-[#7C8B6E]"}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      )}
      <p className="mt-1.5 text-[11.5px] text-[#9A968B] leading-snug">{subtitle}</p>
    </div>
  );
}

